import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Add20Regular, TableLink20Regular } from '@fluentui/react-icons';
import { useDataModels } from '../contexts/DataModelsContext';
import CardListPage from '../components/shared/CardListPage';
import DataModelCard from '../components/data-model-list/DataModelCard';
import ConnectionPickerPanel from '../components/data-model-list/ConnectionPickerPanel';
import type { DataModelListItem } from '../api/dataModelsService';
import { MutationButton, MutationOnly } from '../components/security/WorkspaceAccess';

const useStyles = makeStyles({
  createCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transitionProperty: 'border-color, background-color, box-shadow',
    transitionDuration: '180ms',
    minHeight: '160px',
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
  },
  createIcon: {
    fontSize: '36px',
    color: tokens.colorNeutralForeground4,
  },
  createLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  createSubtitle: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center' as const,
  },
});

const SORT_OPTIONS = [
  { value: 'updated', label: 'Last Updated' },
  { value: 'name', label: 'Name' },
  { value: 'views', label: 'Views' },
  { value: 'datasets', label: 'Datasets' },
  { value: 'status', label: 'Status' },
];

const DataModelListPage: React.FC = () => {
  const styles = useStyles();
  const { dataModels, isLoading, deleteDataModel, loadDataModels } = useDataModels();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('updated');
  const [panelOpen, setPanelOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadDataModels();
  }, [loadDataModels]);

  // Auto-open panel when navigated with ?connectionId=
  useEffect(() => {
    if (searchParams.get('connectionId')) {
      setSearchParams({}, { replace: true });
      setPanelOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subtitle stats
  const subtitle = useMemo(() => {
    const totalViews = dataModels.reduce((sum, m) => sum + m.viewCount, 0);
    const totalDatasets = dataModels.reduce((sum, m) => sum + m.datasetCount, 0);
    return `${dataModels.length} models · ${totalViews} views · ${totalDatasets} datasets`;
  }, [dataModels]);

  // Filter + sort
  const filteredItems = useMemo(() => {
    let items: DataModelListItem[] = dataModels;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.description ?? '').toLowerCase().includes(q) ||
          (m.connectionName ?? '').toLowerCase().includes(q),
      );
    }

    items = [...items].sort((a, b) => {
      switch (sortValue) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'views':
          return b.viewCount - a.viewCount;
        case 'datasets':
          return b.datasetCount - a.datasetCount;
        case 'status':
          return a.status.localeCompare(b.status);
        case 'updated':
        default:
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });

    return items;
  }, [dataModels, searchQuery, sortValue]);

  const handleDelete = useCallback(
    async (id: string) => { await deleteDataModel(id); },
    [deleteDataModel],
  );

  return (
    <>
    <CardListPage
      title="Data Models"
      subtitle={subtitle}
      layout="grid"
      gridMinWidth="280px"
      searchPlaceholder="Search data models..."
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      sortOptions={SORT_OPTIONS}
      sortValue={sortValue}
      onSortChange={setSortValue}
      isLoading={isLoading}
      isEmpty={dataModels.length === 0}
      filteredCount={filteredItems.length}
      persistentCard={(
        <MutationOnly>
          <div
            className={styles.createCard}
            onClick={() => setPanelOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPanelOpen(true); } }}
          >
            <Add20Regular className={styles.createIcon} />
            <Text className={styles.createLabel}>New Data Model</Text>
            <Text className={styles.createSubtitle}>Build or generate a semantic model</Text>
          </div>
        </MutationOnly>
      )}
      emptyIcon={<TableLink20Regular />}
      emptyTitle="No data models yet"
      emptyDescription="Connect a data source or create a semantic model to get started."
      emptyAction={(
        <MutationOnly>
          <MutationButton appearance="primary" icon={<Add20Regular />} onClick={() => setPanelOpen(true)}>
            Create Data Model
          </MutationButton>
        </MutationOnly>
      )}
    >
      {filteredItems.map((item) => (
        <DataModelCard
          key={item.id}
          item={item}
          onClick={() => navigate(`/data-models/${item.id}`)}
          onDelete={handleDelete}
        />
      ))}
    </CardListPage>
    <MutationOnly>
      <ConnectionPickerPanel isOpen={panelOpen} onClose={() => setPanelOpen(false)} />
    </MutationOnly>
    </>
  );
};

export default DataModelListPage;